/**
 * Utility functions for managing resume section ordering
 * Used for drag-and-drop customization of resume layout
 */

// Default section order
export const DEFAULT_SECTION_ORDER = [
  "personalInfo",
  "summary",
  "experience",
  "education",
  "skills",
  "projects",
  "certifications",
  "achievements",
  "languages",
  "awards",
  "volunteering",
  "interests",
  "industryExpertise",
  "publications",
  "references",
];

// Section metadata for display
export const SECTION_METADATA = {
  personalInfo: {
    id: "personalInfo",
    title: "Personal Information",
    icon: "👤",
    description: "Name, contact details, location",
    required: true,
    moveable: false, // Personal info should always be at top
  },
  summary: {
    id: "summary",
    title: "Professional Summary",
    icon: "📄",
    description: "Brief overview of your professional background",
    required: false,
    moveable: true,
  },
  experience: {
    id: "experience",
    title: "Work Experience",
    icon: "💼",
    description: "Your employment history and achievements",
    required: false,
    moveable: true,
  },
  education: {
    id: "education",
    title: "Education",
    icon: "🎓",
    description: "Academic qualifications and degrees",
    required: false,
    moveable: true,
  },
  skills: {
    id: "skills",
    title: "Skills",
    icon: "⚡",
    description: "Technical and professional skills",
    required: false,
    moveable: true,
  },
  projects: {
    id: "projects",
    title: "Projects",
    icon: "🚀",
    description: "Personal and professional projects",
    required: false,
    moveable: true,
  },
  certifications: {
    id: "certifications",
    title: "Certifications",
    icon: "🏆",
    description: "Professional certifications and licenses",
    required: false,
    moveable: true,
  },
  achievements: {
    id: "achievements",
    title: "Achievements",
    icon: "🏅",
    description: "Notable accomplishments and awards",
    required: false,
    moveable: true,
  },
  languages: {
    id: "languages",
    title: "Languages",
    icon: "🌐",
    description: "Language proficiency",
    required: false,
    moveable: true,
  },
  languages: {
    id: "languages",
    title: "Languages",
    icon: "🌐",
    description: "Languages you speak and proficiency levels",
    required: false,
    moveable: true,
  },
  awards: {
    id: "awards",
    title: "Awards",
    icon: "🏆",
    description: "Awards and honors received",
    required: false,
    moveable: true,
  },
  volunteering: {
    id: "volunteering",
    title: "Volunteering",
    icon: "❤️",
    description: "Volunteer work and community involvement",
    required: false,
    moveable: true,
  },
  interests: {
    id: "interests",
    title: "Interests",
    icon: "💡",
    description: "Personal interests and hobbies",
    required: false,
    moveable: true,
  },
  industryExpertise: {
    id: "industryExpertise",
    title: "Industry Expertise",
    icon: "🧪",
    description: "Areas of expertise with proficiency levels",
    required: false,
    moveable: true,
  },
  publications: {
    id: "publications",
    title: "Publications",
    icon: "📖",
    description: "Research papers, articles, or books published",
    required: false,
    moveable: true,
  },
  references: {
    id: "references",
    title: "References",
    icon: "👥",
    description: "Professional references and recommendations",
    required: false,
    moveable: true,
  },
};

/**
 * Get sections present in resume data
 * @param {Object} resumeData - Resume data object
 * @returns {Array} - Array of section IDs present in the data
 */
export const getAvailableSections = (resumeData) => {
  if (!resumeData) return [];

  const availableSections = [];

  // Check each possible section
  Object.keys(SECTION_METADATA).forEach((sectionId) => {
    const data = resumeData[sectionId];

    if (data) {
      // Check if section has data
      if (Array.isArray(data)) {
        if (data.length > 0) availableSections.push(sectionId);
      } else if (typeof data === "object" && data !== null) {
        // Check if object has meaningful data
        const hasData = Object.values(data).some((value) => value !== null && value !== undefined && value !== "");
        if (hasData) availableSections.push(sectionId);
      } else if (data && String(data).trim().length > 0) {
        availableSections.push(sectionId);
      }
    }
  });

  // Also check for custom sections
  if (resumeData.customSections && Array.isArray(resumeData.customSections)) {
    resumeData.customSections.forEach((customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        availableSections.push(customSection.id);
      }
    });
  }

  return availableSections;
};

/**
 * Get metadata for a custom section
 * @param {Object} customSection - Custom section object
 * @returns {Object} - Section metadata object
 */
export const getCustomSectionMetadata = (customSection) => {
  return {
    id: customSection.id,
    title: customSection.title,
    icon: "📝",
    description: "Custom section",
    required: false,
    moveable: true,
    isCustom: true,
  };
};

/**
 * Get custom section title or default
 * @param {string} sectionId - Section ID
 * @param {Object} customTitles - Custom section titles object
 * @returns {string} - Section title (custom or default)
 */
export const getSectionTitle = (sectionId, customTitles = {}) => {
  // Map section IDs to custom title keys
  const titleKeyMap = {
    personalInfo: "personalInfo",
    summary: "summary",
    experience: "experience",
    education: "education",
    skills: "skills",
    projects: "projects",
    certifications: "certifications",
    achievements: "achievements",
    languages: "languages",
    awards: "awards",
    volunteering: "volunteering",
    interests: "interests",
    industryExpertise: "industryExpertise",
    publications: "publications",
    references: "references",
  };

  const titleKey = titleKeyMap[sectionId];
  if (titleKey && customTitles[titleKey]) {
    return customTitles[titleKey];
  }

  return SECTION_METADATA[sectionId]?.title || sectionId;
};

/**
 * Get ordered sections based on custom order or default
 * @param {Object} resumeData - Resume data object
 * @param {Array} customOrder - Custom section order array
 * @param {Object} customTitles - Custom section titles object
 * @returns {Array} - Ordered array of section objects with data
 */
export const getOrderedSections = (resumeData, customOrder = null, customTitles = {}) => {
  const availableSections = getAvailableSections(resumeData);
  const sectionOrder = customOrder || DEFAULT_SECTION_ORDER;

  // Build a map of custom sections for quick lookup
  const customSectionsMap = {};
  if (resumeData?.customSections && Array.isArray(resumeData.customSections)) {
    resumeData.customSections.forEach((cs) => {
      if (cs.id && cs.title && cs.items?.length > 0) {
        customSectionsMap[cs.id] = cs;
      }
    });
  }

  // Track added sections to prevent duplicates
  const addedSections = new Set();

  // Helper to get section metadata (handles both standard and custom sections)
  const getSectionData = (sectionId) => {
    // Check if it's a custom section
    if (customSectionsMap[sectionId]) {
      const customSection = customSectionsMap[sectionId];
      return {
        id: sectionId,
        ...getCustomSectionMetadata(customSection),
        title: customTitles[sectionId] || customSection.title,
        data: customSection.items,
        hasData: true,
        isCustom: true,
      };
    }
    // Standard section
    return {
      id: sectionId,
      ...SECTION_METADATA[sectionId],
      title: getSectionTitle(sectionId, customTitles),
      data: resumeData[sectionId],
      hasData: true,
    };
  };

  // Filter and order sections based on availability and custom order
  const orderedSections = sectionOrder
    .filter((sectionId) => {
      // Skip if already added or not available
      if (addedSections.has(sectionId) || !availableSections.includes(sectionId)) {
        return false;
      }
      addedSections.add(sectionId);
      return true;
    })
    .map((sectionId) => getSectionData(sectionId));

  // Add any sections that exist in data but not in the order
  availableSections.forEach((sectionId) => {
    if (!addedSections.has(sectionId)) {
      addedSections.add(sectionId);
      orderedSections.push(getSectionData(sectionId));
    }
  });

  return orderedSections;
};

/**
 * Reorder sections after drag and drop
 * @param {Array} sections - Current ordered sections
 * @param {number} startIndex - Start index of dragged item
 * @param {number} endIndex - End index of dragged item
 * @returns {Array} - Reordered sections array
 */
export const reorderSections = (sections, startIndex, endIndex) => {
  const result = Array.from(sections);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

/**
 * Get section order array from ordered sections
 * @param {Array} orderedSections - Ordered sections array
 * @returns {Array} - Array of section IDs in order
 */
export const getSectionOrder = (orderedSections) => {
  return orderedSections.map((section) => section.id);
};

/**
 * Apply custom section order to resume data
 * @param {Object} resumeData - Resume data object
 * @param {Array} sectionOrder - Custom section order array
 * @returns {Object} - Resume data with sections in custom order
 */
export const applyCustomSectionOrder = (resumeData, sectionOrder) => {
  if (!sectionOrder || sectionOrder.length === 0) {
    return resumeData;
  }

  const orderedData = {};

  // Add sections in custom order
  sectionOrder.forEach((sectionId) => {
    if (resumeData[sectionId]) {
      orderedData[sectionId] = resumeData[sectionId];
    }
  });

  // Add any remaining sections not in the custom order
  Object.keys(resumeData).forEach((key) => {
    if (!sectionOrder.includes(key)) {
      orderedData[key] = resumeData[key];
    }
  });

  return orderedData;
};

/**
 * Save custom section order to localStorage
 * @param {string} resumeId - Resume ID
 * @param {Array} sectionOrder - Section order array
 */
export const saveCustomSectionOrder = (resumeId, sectionOrder) => {
  try {
    const key = `resume_section_order_${resumeId}`;
    localStorage.setItem(key, JSON.stringify(sectionOrder));
  } catch (error) {
    console.error("Error saving custom section order:", error);
  }
};

/**
 * Load custom section order from localStorage
 * @param {string} resumeId - Resume ID
 * @returns {Array|null} - Saved section order or null
 */
export const loadCustomSectionOrder = (resumeId) => {
  try {
    const key = `resume_section_order_${resumeId}`;
    const saved = localStorage.getItem(key);
    if (!saved) return null;

    const savedOrder = JSON.parse(saved);

    // Ensure all sections from DEFAULT_SECTION_ORDER are included
    // This handles cases where new sections were added after the user saved their order
    const updatedOrder = [...savedOrder];
    DEFAULT_SECTION_ORDER.forEach((sectionId) => {
      if (!updatedOrder.includes(sectionId)) {
        updatedOrder.push(sectionId);
      }
    });

    return updatedOrder;
  } catch (error) {
    console.error("Error loading custom section order:", error);
    return null;
  }
};

/**
 * Clear custom section order from localStorage
 * @param {string} resumeId - Resume ID
 */
export const clearCustomSectionOrder = (resumeId) => {
  try {
    const key = `resume_section_order_${resumeId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error clearing custom section order:", error);
  }
};
